import React, { FC, useEffect, useState } from "react";
import styles from "./Admin.module.scss";
import apiInstance from "../services/httpService";
import Switch from "@mui/material/Switch";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

interface RosterMember {
  user_id: number;
  name: string;
  vocal_part: string | null;
  status: string | null;          // 'present' | 'absent' | 'od' | null (not yet marked)
  subscription_h1: boolean | null; // true = paid, false = unpaid, null = not applicable
  subscription_h2: boolean | null;
}

const STATUS_LABEL: Record<string, string> = {
  present: "PRESENT",
  absent: "ABSENT",
  od:      "ON DUTY",
};

// Numeric rank used when sorting by attendance status (ascending = present first).
const STATUS_SORT: Record<string, number> = {
  present: 1,
  absent:  2,
  od:      3,
  // null / not marked → 4 (always last in ascending)
};

// Preferred tab order for vocal parts (SATB). Unknown parts fall back to
// alphabetical; members with no part get an "Others" tab at the end.
const PART_ORDER: Record<string, number> = {
  soprano: 1,
  alto:    2,
  tenor:   3,
  bass:    4,
};

const MIN_SEARCH_CHARS = 3;

// Current calendar half: months 1-6 → H1, months 7-12 → H2.
const CURRENT_HALF = new Date().getMonth() < 6 ? 1 : 2;

type SortCol = "name" | "status" | "sub_h1" | "sub_h2";
type SortDir = "asc" | "desc";

const MarkAttendance: FC = () => {
  const [roster,       setRoster]       = useState<RosterMember[]>([]);
  const [seasonName,   setSeasonName]   = useState<string | null>(null);
  const [practiceDate, setPracticeDate] = useState<string | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [busyIds,    setBusyIds]    = useState<Set<number>>(new Set());
  const [search,     setSearch]     = useState("");
  const [activeTab,  setActiveTab]  = useState("");
  const [sortCol,    setSortCol]    = useState<SortCol>("name");
  const [sortDir,    setSortDir]    = useState<SortDir>("asc");

  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const token = localStorage.getItem("token");

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const toast = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  const setBusy = (userId: number, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(userId); else next.delete(userId);
      return next;
    });

  const loadRoster = () => {
    apiInstance
      .axiosCall("api/ccc/practice/active", { token }, "POST")
      .then((res: any) => {
        const d = res?.data?.data;
        if (res?.data?.status === "success" && d) {
          setRoster(d.roster || []);
          setSeasonName(d.season_name || null);
          setPracticeDate(d.practice_date || null);
        } else {
          setRoster([]);
        }
        setLoading(false);
      })
      .catch(() => {
        toast("Could not load the roster. Please try again.", "error");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markMember = (member: RosterMember, present: boolean) => {
    const status = present ? "present" : "absent";
    setBusy(member.user_id, true);
    apiInstance
      .axiosCall(
        "api/ccc/practice/attendance/mark",
        { token, user_id: member.user_id, status },
        "POST"
      )
      .then((res: any) => {
        setBusy(member.user_id, false);
        if (res?.data?.status === "success") {
          setRoster((prev) =>
            prev.map((m) => (m.user_id === member.user_id ? { ...m, status } : m))
          );
          toast(`${member.name} marked ${status}.`, "success");
        } else {
          toast(res?.data?.message || "Could not mark attendance.", "error");
        }
      })
      .catch((err: any) => {
        setBusy(member.user_id, false);
        toast(err?.response?.data?.message || "Could not mark attendance.", "error");
      });
  };

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.Admin__loading}>
        <CircularProgress />
      </div>
    );
  }

  // ── Derived data ─────────────────────────────────────────────────────────────

  const query = search.trim().toLowerCase();

  // Filter by search query, then sort by the active column/direction.
  const applyView = (list: RosterMember[]): RosterMember[] => {
    const filtered =
      query.length >= MIN_SEARCH_CHARS
        ? list.filter((m) => m.name.toLowerCase().includes(query))
        : list;

    // Subscription sort: paid (true=1) before unpaid (false/null=0), tie-break by name.
    const subRank = (v: boolean | null) => (v ? 1 : 0);

    return filtered.slice().sort((a, b) => {
      let cmp = 0;
      if (sortCol === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortCol === "status") {
        const ra = STATUS_SORT[a.status || ""] ?? 4;
        const rb = STATUS_SORT[b.status || ""] ?? 4;
        cmp = ra - rb;
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortCol === "sub_h1") {
        cmp = subRank(b.subscription_h1) - subRank(a.subscription_h1);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      } else if (sortCol === "sub_h2") {
        cmp = subRank(b.subscription_h2) - subRank(a.subscription_h2);
        if (cmp === 0) cmp = a.name.localeCompare(b.name);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  };

  const partRank = (p: string) =>
    p === "Others" ? 1000 : PART_ORDER[p.toLowerCase()] ?? 500;

  const availableParts = Array.from(
    new Set(roster.map((m) => (m.vocal_part || "").trim()).filter((p) => p !== ""))
  ).sort((a, b) => partRank(a) - partRank(b) || a.localeCompare(b));

  const hasOthers = roster.some((m) => !(m.vocal_part || "").trim());

  const categorize = availableParts.length >= 1;
  const sections   = categorize
    ? [...availableParts, ...(hasOthers ? ["Others"] : [])]
    : [];
  const activeSection = sections.includes(activeTab) ? activeTab : sections[0];

  const membersOf = (section: string) =>
    section === "Others"
      ? roster.filter((m) => !(m.vocal_part || "").trim())
      : roster.filter((m) => (m.vocal_part || "").trim() === section);

  const sectionCounts:  Record<string, number> = {};
  const sectionPresent: Record<string, number> = {};
  roster.forEach((m) => {
    const key = (m.vocal_part || "").trim() || "Others";
    sectionCounts[key]  = (sectionCounts[key]  || 0) + 1;
    if (m.status === "present")
      sectionPresent[key] = (sectionPresent[key] || 0) + 1;
  });

  const shown = applyView(categorize ? membersOf(activeSection) : roster);

  // Small indicator shown next to the sorted column header.
  const sortIndicator = (col: SortCol) =>
    sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  // Subscription badge: green = paid, red = unpaid + current half, grey = unpaid + other half.
  const subBadge = (paid: boolean | null, half: 1 | 2) => {
    if (paid) return styles.Admin__attendance_sub_paid;
    return CURRENT_HALF === half
      ? styles.Admin__attendance_sub_due
      : styles.Admin__attendance_sub_na;
  };

  const subLabel = (paid: boolean | null) =>
    paid ? "PAID" : "UNPAID";

  const formattedDate = (() => {
    if (!practiceDate) return null;
    const d = new Date(practiceDate);
    if (isNaN(d.getTime())) return practiceDate;
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className={styles.Admin__attendance}>
      {seasonName && (
        <div className={styles.Admin__attendance_season}>{seasonName}</div>
      )}

      {roster.length === 0 ? (
        <div className={styles.Admin__empty}>
          No choristers to mark for this practice.
        </div>
      ) : (
        <>
          {/* Tabs */}
          {categorize && (
            <div className={styles.Admin__attendance_tabs}>
              {sections.map((section) => (
                <button
                  key={section}
                  type="button"
                  className={`${styles.Admin__attendance_tab} ${
                    section === activeSection
                      ? styles.Admin__attendance_tab_active
                      : ""
                  }`}
                  onClick={() => setActiveTab(section)}
                >
                  {section}
                  <span
                    className={styles.Admin__attendance_tab_count}
                    title={`${sectionPresent[section] || 0} present of ${
                      sectionCounts[section] || 0
                    }`}
                  >
                    {sectionPresent[section] || 0}/{sectionCounts[section] || 0}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Search + practice date */}
          <div className={styles.Admin__attendance_search_row}>
            <TextField
              size="small"
              variant="outlined"
              label="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              className={styles.Admin__attendance_search_input}
            />
            {formattedDate && (
              <div className={styles.Admin__attendance_date}>
                {formattedDate}
              </div>
            )}
          </div>

          {/* Table */}
          {shown.length === 0 ? (
            <div className={styles.Admin__empty}>
              {query.length >= MIN_SEARCH_CHARS
                ? "No choristers match your search."
                : "No choristers in this section."}
            </div>
          ) : (
            <div className={styles.Admin__attendance_table_wrap}>
              <table className={styles.Admin__attendance_table}>
                <thead>
                  <tr>
                    <th
                      data-sortable="true"
                      onClick={() => handleSort("name")}
                    >
                      Name{sortIndicator("name")}
                    </th>
                    <th>Mark</th>
                    <th
                      data-sortable="true"
                      onClick={() => handleSort("status")}
                    >
                      Status{sortIndicator("status")}
                    </th>
                    <th
                      data-sortable="true"
                      onClick={() => handleSort("sub_h1")}
                    >
                      Subscription H1{sortIndicator("sub_h1")}
                    </th>
                    <th
                      data-sortable="true"
                      onClick={() => handleSort("sub_h2")}
                    >
                      Subscription H2{sortIndicator("sub_h2")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((member) => {
                    const isPresent = member.status === "present";
                    const busy      = busyIds.has(member.user_id);
                    const badgeClass = isPresent
                      ? styles.Admin__attendance_badge_present
                      : member.status === "absent"
                      ? styles.Admin__attendance_badge_absent
                      : styles.Admin__attendance_badge_none;

                    return (
                      <tr key={member.user_id}>
                        {/* Name (+ vocal part as subtitle when no tabs) */}
                        <td>
                          <span className={styles.Admin__attendance_table_name}>
                            {member.name}
                          </span>
                          {!categorize && member.vocal_part && (
                            <span className={styles.Admin__attendance_table_part}>
                              {member.vocal_part}
                            </span>
                          )}
                        </td>

                        {/* Toggle */}
                        <td>
                          <Switch
                            size="small"
                            checked={isPresent}
                            disabled={busy}
                            onChange={(e) =>
                              markMember(member, e.target.checked)
                            }
                          />
                        </td>

                        {/* Status badge */}
                        <td>
                          <span
                            className={`${styles.Admin__attendance_badge} ${badgeClass}`}
                          >
                            {member.status
                              ? STATUS_LABEL[member.status] ||
                                member.status.toUpperCase()
                              : "NOT MARKED"}
                          </span>
                        </td>

                        {/* Subscription H1 */}
                        <td>
                          <span className={subBadge(member.subscription_h1, 1)}>
                            {subLabel(member.subscription_h1)}
                          </span>
                        </td>

                        {/* Subscription H2 */}
                        <td>
                          <span className={subBadge(member.subscription_h2, 2)}>
                            {subLabel(member.subscription_h2)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MarkAttendance;
